import React from "react";
import Select from "react-select";

// Days in the given month; February has 29 until a non-leap year is chosen.
export const getDaysInMonth = (month, year) => {
  if (!month) return 31;
  // Day 0 of the next month is the last day of this month. 2000 is a leap year.
  return new Date(year || 2000, month, 0).getDate();
};

const toOptions = (values, labelFor = (v) => v) =>
  values.map((value) => ({ value, label: labelFor(value) }));

const range = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

const currentYear = new Date().getFullYear();
const yearOptions = toOptions(range(currentYear - 105, currentYear).reverse());

const defaultStyles = {
  control: (provided) => ({
    ...provided,
    borderColor: "#195174",
    marginBottom: "10px",
  }),
};

/**
 * Month / Day / Year date of birth dropdowns.
 *
 * The day list follows the selected month and year (30/31 days, 28/29 for
 * February in leap years). If the selected day no longer exists after the
 * month or year changes, the day is cleared.
 *
 * @param {{month: number|'', day: number|'', year: number|''}} value
 * @param {(dob: object) => void} onChange  Receives the full next value.
 * @param {(month: number) => string} [monthLabel]  Label for each month.
 */
const DobSelect = ({
  value,
  onChange,
  monthLabel,
  styles = defaultStyles,
  style,
}) => {
  const { month, day, year } = value;
  const monthOptions = toOptions(range(1, 12), monthLabel);
  const dayOptions = toOptions(range(1, getDaysInMonth(month, year)));

  const handleChange = (name) => (option) => {
    const next = { ...value, [name]: option.value };
    if (next.day && next.day > getDaysInMonth(next.month, next.year)) {
      next.day = "";
    }
    onChange(next);
  };

  // Render menus in a portal so a parent with overflow hidden (accordions,
  // chat panels) cannot clip the end of the list.
  const selectProps = {
    styles: {
      ...styles,
      menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    },
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPlacement: "auto",
  };

  return (
    <div
      style={{ display: "flex", gap: "10px", marginBottom: "10px", ...style }}
    >
      <Select
        {...selectProps}
        options={monthOptions}
        value={monthOptions.find((o) => o.value === month) || null}
        onChange={handleChange("month")}
        placeholder="Month"
        aria-label="Birth month"
      />
      <Select
        {...selectProps}
        options={dayOptions}
        value={dayOptions.find((o) => o.value === day) || null}
        onChange={handleChange("day")}
        placeholder="Day"
        aria-label="Birth day"
      />
      <Select
        {...selectProps}
        options={yearOptions}
        value={yearOptions.find((o) => o.value === year) || null}
        onChange={handleChange("year")}
        placeholder="Year"
        aria-label="Birth year"
      />
    </div>
  );
};

export default DobSelect;
